import { CheckoutRules } from '../../interfaces'
import { readString, ScalarActionArgs } from '../../utils/actionArgs'
import { AlertMessageType } from '../../utils/getAlertErrorMessage'
import useCheckoutRuleSelection from './useCheckoutRuleSelection'

interface SetPaymentMethodArgs extends ScalarActionArgs {
  methodId?: unknown
}

/**
 * The EventManager actions the payment-method screen dispatches.
 *
 * Rendering lives entirely in SCS config, so these are the only seams between
 * the authored component tree and the checkout data layer.
 *
 * The write path itself is `useCheckoutRuleSelection`, shared with the
 * delivery screen. What stays here is the part a storefront authors against:
 * the handler names, and the field its args arrive under.
 */
export const usePaymentMethodActions = () => {
  const { select, confirm, back } = useCheckoutRuleSelection(
    CheckoutRules.PAYMENT_METHODS,
    AlertMessageType.SetPaymentMethod,
  )

  /**
   * Row tap.
   *
   * The picker calls back with the option's id alone, so a storefront authors
   * `args: { methodId: '{{this.value}}' }`. Reading both names accepts a
   * component that calls back with an object too.
   */
  const setPaymentMethod = (args: SetPaymentMethodArgs = {}) =>
    select(readString(args.methodId, args.value))

  return {
    setPaymentMethod,
    confirmPaymentMethod: confirm,
    backFromPaymentMethod: back,
    /*
     * The bare write, for COM-1501's `startGuidedWalk`, which calls it with
     * `{ silent: true }`. Handed out so the walk reuses this instance
     * instead of mounting another `useCheckoutRuleSelection` per container.
     */
    selectPaymentMethod: select,
  }
}

export default usePaymentMethodActions
